/**
 * Activations list — /w/:slug/activations (flag-gated by RequireAudiences).
 *
 * Workspace-wide: every audience this workspace is shipping and what changed on each side. The v2 IA
 * makes this a peer of Audiences rather than a tab inside one, because an activation belongs to the
 * workspace and one audience has many — so the audience is a first-class fact on every row and the
 * question "what is failing right now?" is answerable on one screen.
 *
 * The audience is no longer a COLUMN, as this paragraph used to say: it folded into the identifier
 * strip at the head of each row, where it sits beside the destination audience it pairs with. It is
 * still a search field, a filter facet and a sort key in the toolbar — what it stopped being is a
 * column three across restating what the strip already says.
 *
 * Rows carry `audienceId`, never an audience name — names resolve at render through `audienceNames`,
 * so renaming an audience moves it everywhere and a broken reference looks broken instead of silently
 * rendering a stale string.
 *
 * A row is TITLED by the audience on the far side (`destinationAudienceLabel`), because two syncs into
 * the same ad account are otherwise indistinguishable. That name is not always known — see the ladder
 * in `lib/activation-list.ts` — so the title degrades to the far-side id and then to the account, and
 * the list never blocks on it: `listActivationMeta` is outside both `isLoading` and `error`.
 *
 * Built on the shared list-page pattern (`useListControls` + `ListToolbar` + `filterSortItems` +
 * `Table` / `VirtualizedCards`), the same one Workflows uses.
 */
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Send } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { REPAIR_AUDIENCE_FIRST } from '@/_shared/components/AccountRepair/copy';
import { ListEmpty, ListError, ListNoMatches, ListSkeleton } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { VirtualizedCards } from '@/_shared/components/list/VirtualizedCards';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useSiblingActionsOff } from '@/_shared/hooks/useSiblingStatus';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';
import { LazyAudienceActivationSheet } from '@/audiences/components/LazyAudienceActivationSheet';
import {
  AUDIENCE_STATE_UNREADABLE,
  type AudienceRepairAnswer,
  useAudienceNeedsRepairBySource,
} from '@/audiences/hooks/useAudienceRepairBySource';

import { ActivationCard } from '../components/ActivationCard';
import { ActivationsTable } from '../components/ActivationsTable';
import { DeleteActivationDialog } from '../components/DeleteActivationDialog';
import { useDeleteActivation } from '../hooks/useDeleteActivation';
import {
  ACTIVATION_DEFAULT_SORT,
  ACTIVATION_FILTER_KEYS,
  ACTIVATION_SORT_KEYS,
  ACTIVATION_SORT_OPTIONS,
  type ActivationFilterKey,
  activationPath,
  type ActivationSortKey,
  audienceLabel,
  buildActivationFilters,
  destinationAudienceLabel,
  makeActivationDeriveConfig,
} from '../lib/activation-list';
import { useActivations } from '../lib/use-activations';
import type { ActivationRow } from '../types';

// Cycled by the toolbar; the order is the order the search fields are ranked in, so the first hint a
// reader sees names the field the rows are now titled by.
const ITEM_NOUN = { singular: 'activation', plural: 'activations' };

/** One definition for the row's destination, so the card's link and the kebab's action can't diverge. */
const runsPath = (row: ActivationRow): string => activationPath(row.audienceId, row.syncId);

const getItemKey = (row: ActivationRow): string => row.syncId;

/** Why Edit waits on the row's audience (ACT2-1286): it needs repair, or its state is still loading. */
function repairReasonOf(answer: AudienceRepairAnswer): string | undefined {
  if (answer === 'needs_repair') return REPAIR_AUDIENCE_FIRST;
  if (answer === 'failed') return AUDIENCE_STATE_UNREADABLE;
  return answer === 'unknown' ? 'Loading this activation…' : undefined;
}

export function ActivationsList(): React.ReactElement {
  usePageTitle('Activations');
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const navigate = useNavigate();
  const { rows, audienceNames, audiences, isLoading, error, refetch } = useActivations();

  // Read once here, the way `useChallengeCreators` reads it, and threaded down beside `onEdit` to both
  // row views — a demo workspace performs zero RudderStack writes, so Edit must not reach
  // `readActivationConfig` (which refuses it server-side with a CONFLICT) from any of the three places
  // it is offered: this table, these cards, and the detail page's own kebab.
  const { data: workspaceData } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const challengeMode = workspaceData?.challengeMode ?? false;
  // ACT2-1124: a disabled or gone sibling turns activation actions off; the banner says why.
  const siblingActions = useSiblingActionsOff();
  /**
   * Why `Edit` is unavailable, for BOTH row views — the same shape `ActivationDetail` uses.
   *
   * A REASON rather than a `demo` boolean, because the two cases need different words: while the answer
   * is missing the control must stay off, and saying "not available in the demo workspace" to a real
   * workspace is a claim, not a wait. `workspaceData === undefined` covers a FAILED `workspace.get` as
   * well as a pending one — TanStack's `isPending` is phase only, so an errored query reads as
   * "finished, answer no" and would ship a live Edit into a demo workspace.
   */
  //
  // ACT2-1124 LLD §5.9: a disabled or gone sibling turns activation actions off — Edit and Delete both — with the
  // banner's text as the reason. It comes first: no other state makes either action possible then.
  const editDisabledReason = siblingActions.off
    ? siblingActions.reason
    : workspaceData === undefined
      ? 'Loading this activation…'
      : challengeMode
        ? 'Editing is not available in the demo workspace — delete this activation and create a new one'
        : undefined;
  const deleteDisabledReason = siblingActions.off ? siblingActions.reason : undefined;
  // ACT2-1286: Edit waits for its audience's repair; Run sync and Delete stay allowed.
  const audienceNeedsRepair = useAudienceNeedsRepairBySource();
  const editDisabledReasonFor = useCallback(
    (row: ActivationRow): string | undefined =>
      editDisabledReason ?? repairReasonOf(audienceNeedsRepair(row.audienceId)),
    [editDisabledReason, audienceNeedsRepair],
  );

  // Audience + Platform options come from the rows on screen, so the filter menu never offers a facet
  // that matches nothing. `values` additionally lets useListControls drop a hand-edited URL param.
  const { groups: filterGroups, values: filterValues } = useMemo(
    () => buildActivationFilters(rows, audiences),
    [rows, audiences],
  );

  const controls = useListControls<ActivationSortKey, ActivationFilterKey>({
    sortKeys: ACTIVATION_SORT_KEYS,
    defaultSort: ACTIVATION_DEFAULT_SORT,
    // The table is the default view: an activation is read by comparison down a column.
    defaultView: 'list',
    filterKeys: ACTIVATION_FILTER_KEYS,
    filterOptions: filterValues,
    persistKey: 'activations',
  });

  const deriveConfig = useMemo(() => makeActivationDeriveConfig(audienceNames), [audienceNames]);
  // `controls.query` is already debounced by the toolbar's search box, so this recomputes on the
  // debounce, not per keystroke. `view` is deliberately not a dependency — it changes how rows are
  // drawn, never which rows there are.
  const visible = useMemo(
    () =>
      filterSortItems(rows, { query: controls.query, sort: controls.sort, filters: controls.filters }, deriveConfig),
    [rows, controls.query, controls.sort, controls.filters, deriveConfig],
  );

  // Stable identities: a fresh lambda each render would make the virtualized table / card grid
  // reconcile every mounted row on unrelated re-renders (e.g. each search keystroke).
  const openRuns = useCallback((row: ActivationRow) => navigate(wp(runsPath(row))), [navigate, wp]);

  // The row being confirmed, not just its id: the dialog names the destination it ships into, and
  // looking that back up from the id would mean re-deriving a label the row already carries.
  const [pendingDelete, setPendingDelete] = useState<ActivationRow | null>(null);
  const { remove, isPending } = useDeleteActivation(() => setPendingDelete(null));
  const askDelete = useCallback((row: ActivationRow) => setPendingDelete(row), []);
  /**
   * The sync being edited. Owned by the page for the same reason `pendingDelete` is: the sheet must
   * outlive the row, which unmounts the moment it scrolls out of the virtualized list.
   */
  const [editing, setEditing] = useState<ActivationRow | null>(null);
  const askEdit = useCallback((row: ActivationRow) => setEditing(row), []);

  const renderCard = useCallback(
    (row: ActivationRow) => (
      <ActivationCard
        row={row}
        audienceName={audienceLabel(row, audienceNames)}
        to={wp(runsPath(row))}
        onDelete={askDelete}
        onEdit={askEdit}
        editDisabledReason={editDisabledReasonFor(row)}
        deleteDisabledReason={deleteDisabledReason}
      />
    ),
    [wp, audienceNames, askDelete, askEdit, editDisabledReasonFor, deleteDisabledReason],
  );

  const hasActivations = rows.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <header className="flex items-start gap-3 border-b px-6 py-4">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Activations</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Every audience this workspace is shipping, and what changed on each side.
          </p>
        </div>
      </header>

      {error && !hasActivations ? (
        // Only take over the page when there is nothing to show. A failed BACKGROUND refetch (window
        // refocus past staleTime, or a post-write invalidation) keeps the cached rows but sets `error` —
        // blanking a still-usable list behind a full-screen error would be the worse failure.
        <ListError
          title="Could not load activations"
          message={`The request to list syncs failed. Nothing has changed — the activations themselves are still running. (${error.message})`}
        >
          <Button variant="secondary" onClick={refetch} Icon={<RefreshCw />} label="Retry" />
        </ListError>
      ) : isLoading ? (
        // Matches the `max-w-7xl` the toolbar and both view regions below use — without it the skeleton
        // draws at the shared 5xl default and the page snaps 16rem wider the moment the rows land.
        <ListSkeleton view={controls.view} maxWidthClass="max-w-7xl" />
      ) : !hasActivations ? (
        <ListEmpty
          icon={Send}
          title="Nothing is being activated yet"
          description="An activation ships one saved audience to one ad-platform audience on a schedule, and reports what changed on each side after every run."
        />
      ) : (
        <>
          {/* `challengeMode`, not `demo`: a control fails closed while the flag is unknown, a claim must
              not — that would call a real workspace's runs sample data. */}
          {challengeMode && (
            <div className="mx-auto w-full max-w-7xl px-6 pt-4">
              <Callout variant="info">
                No sync runs in a demo workspace. The Last run column below was generated to fill this page, and nothing
                was delivered.
              </Callout>
            </div>
          )}

          {error && (
            // The list is a per-audience fan-out, so ONE audience's syncs can fail while the rest load.
            // Without this the page would render a confident, complete-looking workspace-wide list with
            // an audience's activations silently missing — on a screen whose job is "what is failing
            // right now?", under-reporting is the worst failure it has. Persistent + in-context, so a
            // Callout rather than a toast.
            <div className="mx-auto w-full max-w-7xl px-6 pt-4">
              <Callout variant="error">
                Some activations could not be loaded, so this list may be incomplete. ({error.message})
              </Callout>
            </div>
          )}

          <div className="pt-4 pb-1">
            <div className="mx-auto max-w-7xl px-6">
              <ListToolbar
                query={controls.query}
                onQueryChange={controls.setQuery}
                searchPlaceholder="Search activations…"
                view={controls.view}
                onViewChange={controls.setView}
                sortOptions={ACTIVATION_SORT_OPTIONS}
                sort={controls.sort}
                onSortChange={(next) => controls.setSort(next as SortState<ActivationSortKey>)}
                defaultSort={ACTIVATION_DEFAULT_SORT}
                filterGroups={filterGroups}
                filters={controls.filters}
                onFilterChange={(key, value) => controls.setFilter(key as ActivationFilterKey, value)}
                onClearFilters={controls.clearFilters}
                onClearAll={controls.clearAll}
                // The denominator rule, in the toolbar: "5 activations", and "2 of 5 activations" the
                // moment a search or a filter narrows it.
                totalCount={rows.length}
                resultCount={visible.length}
                itemNoun={ITEM_NOUN}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {visible.length === 0 ? (
              <ListNoMatches
                noun="activations"
                onClear={() => {
                  controls.setQuery('');
                  controls.clearFilters();
                }}
              />
            ) : controls.view === 'list' ? (
              <div className="mx-auto h-full max-w-7xl px-6 py-4">
                <ActivationsTable
                  rows={visible}
                  audienceNames={audienceNames}
                  onOpen={openRuns}
                  onEdit={askEdit}
                  onDelete={askDelete}
                  editDisabledReasonFor={editDisabledReasonFor}
                  deleteDisabledReason={deleteDisabledReason}
                />
              </div>
            ) : (
              <VirtualizedCards
                items={visible}
                getItemKey={getItemKey}
                minColumnRem={22}
                maxColumns={3}
                maxContentRem={80}
                estimateRowHeight={190}
                className="h-full"
                renderItem={renderCard}
              />
            )}
          </div>
        </>
      )}

      {/* Owned by the page rather than the row: a dialog rendered inside a virtualized row unmounts
          the moment that row scrolls out of view, taking the confirmation with it. */}
      {/* Same ownership rule as the dialog below. Mounted only while a row is selected so the edit
          fetch fires on open rather than on every list render. */}
      {editing !== null ? (
        <LazyAudienceActivationSheet
          mode="edit"
          open
          onOpenChange={(open) => !open && setEditing(null)}
          syncId={editing.syncId}
          audienceLabel={audienceLabel(editing, audienceNames)}
          warehouseType={editing.warehouseType}
        />
      ) : null}

      <DeleteActivationDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        destinationLabel={pendingDelete === null ? '' : destinationAudienceLabel(pendingDelete)}
        loading={isPending}
        onConfirm={() => pendingDelete !== null && remove(pendingDelete.syncId)}
      />
    </div>
  );
}
